import React, { createContext, useContext, useMemo } from "react";
import { ChargeFormStateProviderProps } from "../types";
import { Project } from "~/gql/types";
import { validateProjectDate } from "../functions/validateProjectDate";
import { deriveFieldStates, FieldStates } from "../functions/deriveFieldStates";

type ChargeFormStateContextData = {
  fieldStates: FieldStates;
  validateProjectDate: (
    project: Project,
    selectedDate: any
  ) => string | undefined;
};

const ChargeFormStateContext = createContext<ChargeFormStateContextData | null>(
  null
);

/**
 * Derives per-field `fieldStates` from the current values and the charge's
 * capability flags, and passes them to the charge form's field components,
 * along with `validateProjectDate`. See the `charge-form-editor-providers` spec.
 */
export const ChargeFormStateProvider: React.FC<ChargeFormStateProviderProps> = (
  props
) => {
  const { children, charge, values, showProjectField } = props;

  // figure out how to consolidate initial charge into values for initial form state

  const stateContextValue = useMemo(
    () => {
      const dependencies = {
        useCustomRate: values.useCustomRate,
        date: values.date,
        project: values.project,
        rateSheetName: values.rateSheetName,
      };

      return {
        fieldStates: deriveFieldStates(dependencies, charge, showProjectField),
        validateProjectDate: validateProjectDate,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values.useCustomRate, values.date, values.project, charge]
  );

  return (
    <ChargeFormStateContext.Provider value={stateContextValue}>
      {children}
    </ChargeFormStateContext.Provider>
  );
};

const useChargeFormStateContext = (fn: string): ChargeFormStateContextData => {
  const context = useContext(ChargeFormStateContext);

  if (!context) {
    throw new Error(
      `Cannot call ${fn} outside of a ChargeFormStateProvider wrapper`
    );
  }

  return context;
};

export function useChargeFormStateApi(): ChargeFormStateContextData {
  const { fieldStates, validateProjectDate } = useChargeFormStateContext(
    "useChargeFormStateApi"
  );

  return {
    fieldStates,
    validateProjectDate,
  };
}
